import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@roger/ui-components';
import { ArrowLeft, ChevronDown } from 'lucide-react';

import { useStore } from '../../store';
import { conversationChannelId } from './memberEntries';
import { MemberRow } from './MemberRow';
import { useGroupDetail } from './useGroupAdminQueries';

type GroupDetailsDrawerProps = {
  groupId: string;
  onClose: () => void;
};

/** Read-only drawer for an automatic or archived group. */
export function GroupDetailsDrawer({
  groupId,
  onClose,
}: GroupDetailsDrawerProps) {
  const { data: detail } = useGroupDetail(groupId);
  const openChatWindow = useStore(state => state.openChatWindow);
  const userId = useStore(state => state.user?.id);
  const channelId = detail ? conversationChannelId(detail, userId) : null;

  return (
    <Sheet open onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent
        side='right'
        aria-describedby={undefined}
        className='flex w-full flex-col overflow-y-auto sm:max-w-none md:w-[420px] lg:w-[480px]'
      >
        <Button
          variant='ghost'
          size='icon'
          aria-label='Back'
          className='md:hidden'
          onClick={onClose}
        >
          <ArrowLeft />
        </Button>
        <SheetHeader>
          <SheetTitle>{detail?.name ?? ''}</SheetTitle>
        </SheetHeader>
        {detail && (
          <div className='flex min-w-0 flex-1 flex-col gap-4'>
            {detail.rule && (
              <div className='flex flex-col gap-1'>
                <p className='text-sm'>{detail.rule}</p>
                <p className='text-xs text-muted-foreground'>
                  Updated automatically
                </p>
              </div>
            )}
            {detail.archived_at && (
              <p className='text-xs text-muted-foreground'>Archived</p>
            )}
            <Collapsible>
              <CollapsibleTrigger className='flex w-full items-center justify-between text-sm font-medium'>
                {`Everyone in this group (${detail.member_count})`}
                <ChevronDown className='h-4 w-4' />
              </CollapsibleTrigger>
              <CollapsibleContent className='mt-2 flex flex-col gap-1'>
                {detail.members.map(member => (
                  <MemberRow key={member.user_id} member={member} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
        <SheetFooter className='flex-row justify-start'>
          <div className='flex flex-col items-start'>
            <Button
              variant='link'
              disabled={!channelId}
              onClick={() => {
                if (!channelId) return;
                // Close first: the chat sits under the drawer's scrim.
                onClose();
                openChatWindow(channelId);
              }}
            >
              Open conversation
            </Button>
            {detail?.channel_id && !channelId && (
              <p className='text-xs text-muted-foreground'>
                Add yourself to open this conversation
              </p>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
